import { useState } from "react";
import { invokeAdmin, errMsg } from "../lib/adminApi";
import { useAdminQuery } from "../hooks/useAdminQuery";
import { Banner, CopyId, PillTabs } from "../components/ui";

type Kind = "posts" | "comments" | "reviews" | "photos" | "event_questions";
type ContentStatus = "visible" | "hidden";

// Rows differ per table; this is the superset of columns we render. Each kind
// populates only the relevant fields.
type ContentRow = {
  id: string;
  status: string;
  created_at: string;
  author_id?: string;
  body?: string | null;
  url?: string;
  entity_kind?: string;
  entity_id?: string;
  post_id?: string;
  event_id?: string;
  rating?: number;
};

const KINDS: { key: Kind; label: string }[] = [
  { key: "posts", label: "Posts" },
  { key: "comments", label: "Comments" },
  { key: "reviews", label: "Reviews" },
  { key: "photos", label: "Photos" },
  { key: "event_questions", label: "Q&A" },
];

const STATUSES: { key: ContentStatus; label: string }[] = [
  { key: "visible", label: "Visible" },
  { key: "hidden", label: "Hidden" },
];

function truncate(s: string | null | undefined, n = 140): string {
  if (!s) return "—";
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

// The id columns that link a row back to an entity / parent post / event.
function linkage(row: ContentRow): string {
  if (row.post_id) return `post ${row.post_id}`;
  if (row.event_id) return `event ${row.event_id}`;
  if (row.entity_kind || row.entity_id)
    return `${row.entity_kind ?? "?"} ${row.entity_id ?? "?"}`;
  return "—";
}

export default function PublishedContentPage() {
  const [kind, setKind] = useState<Kind>("posts");
  const [status, setStatus] = useState<ContentStatus>("visible");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Cancellation-safe fetch keyed on kind+status (ADM-5).
  const { data, loading, error, refetch } = useAdminQuery(
    () =>
      invokeAdmin<{ rows: ContentRow[] }>("admin_published_content_list", {
        kind,
        status,
        limit: 50,
      }),
    [kind, status],
  );
  const rows = data?.rows ?? [];

  async function moderate(id: string, next: ContentStatus) {
    setActionLoading(id);
    setActionError(null);
    try {
      await invokeAdmin("admin_published_content_update", {
        id,
        kind,
        status: next,
      });
      refetch();
    } catch (e) {
      setActionError(errMsg(e));
    } finally {
      setActionLoading(null);
    }
  }

  const isPhotos = kind === "photos";

  return (
    <div>
      <h1 className="text-xl font-semibold">Content</h1>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <PillTabs tabs={KINDS} active={kind} onChange={setKind} />
        <span className="mx-1 text-zinc-700">|</span>
        <PillTabs tabs={STATUSES} active={status} onChange={setStatus} />
      </div>

      {error && <Banner className="mt-3">{error}</Banner>}
      {actionError && <Banner className="mt-3">{actionError}</Banner>}

      <div className="mt-4 rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">{isPhotos ? "Photo" : "Body"}</th>
              <th className="px-3 py-2 font-medium">Linkage</th>
              <th className="px-3 py-2 font-medium">Author</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Created</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-zinc-800 align-top">
                <td className="px-3 py-2 text-zinc-300 text-xs max-w-xs">
                  {isPhotos ? (
                    r.url ? (
                      <img
                        src={r.url}
                        alt=""
                        className="h-16 w-16 rounded object-cover"
                      />
                    ) : (
                      "—"
                    )
                  ) : (
                    <span className="whitespace-pre-wrap break-words">
                      {kind === "reviews" && r.rating != null
                        ? `★${r.rating} — ${truncate(r.body)}`
                        : truncate(r.body)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-zinc-400 font-mono text-xs">
                  {linkage(r)}
                </td>
                <td className="px-3 py-2">
                  <CopyId id={r.author_id} />
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${
                      r.status === "hidden"
                        ? "bg-amber-900 text-amber-200"
                        : "bg-zinc-800 text-zinc-300"
                    }`}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-zinc-400 text-xs">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2">
                  {r.status === "hidden" ? (
                    <button
                      onClick={() => moderate(r.id, "visible")}
                      disabled={actionLoading === r.id}
                      className="rounded bg-zinc-700 px-2 py-0.5 text-xs text-white hover:bg-zinc-600 disabled:opacity-50"
                    >
                      {actionLoading === r.id ? "…" : "Restore"}
                    </button>
                  ) : (
                    <button
                      onClick={() => moderate(r.id, "hidden")}
                      disabled={actionLoading === r.id}
                      className="rounded bg-red-800 px-2 py-0.5 text-xs text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionLoading === r.id ? "…" : "Hide"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-sm text-zinc-500">
                  No {status} {kind}.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-sm text-zinc-500">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
